import ReactSelect, {
    ActionMeta,
    GroupBase,
    MultiValue,
    OptionProps,
    StylesConfig,
    ValueContainerProps,
    components,
} from "react-select"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck } from "@fortawesome/pro-solid-svg-icons"
import React, { FunctionComponent, useMemo } from "react"
import { faSquare } from "@fortawesome/pro-regular-svg-icons"
import styles from "./styles.module.scss"

// documentation: https://react-select.com/home
export type ReactMultiSelectOptionT = {
    value: string
    label: string
    isSelected: boolean
    isDisabled: boolean
}

interface MultiSelectI {
    options: ReactMultiSelectOptionT[]
    onChange: (
        newValue: MultiValue<ReactMultiSelectOptionT>,
        actionMeta: ActionMeta<ReactMultiSelectOptionT>
    ) => void
    width?: string // set the width of the component, if not set default is 400px
    menuPortalTarget?: HTMLElement | null | undefined
    isClearable?: boolean
    styles?:
        | StylesConfig<
              ReactMultiSelectOptionT,
              true,
              GroupBase<ReactMultiSelectOptionT>
          >
        | undefined
    orientation?: "horizontal" | "vertical"
    label?: string | React.ReactNode
    // onMenuClose can be used on menu close to sort order of options after close of menu
    onMenuClose?: () => void
    placeholder?: string
    disabled?: boolean
}

const ReactMultiSelect: FunctionComponent<MultiSelectI> = ({
    options,
    onChange,
    width = "400px",
    isClearable = true,
    menuPortalTarget,
    styles: customStyles,
    orientation = "vertical",
    label,
    onMenuClose,
    placeholder = "Select",
    disabled,
}) => {
    const selectedOptions = useMemo(() => {
        return options.filter((option) => option.isSelected)
    }, [options])

    return (
        <div
            className={
                orientation === "horizontal"
                    ? styles.horizontal
                    : styles.vertical
            }
        >
            {label && (
                <label
                    className={
                        orientation === "horizontal"
                            ? styles.horizontalLabel
                            : styles.verticalLabel
                    }
                >
                    {label}
                </label>
            )}
            <ReactSelect
                menuPortalTarget={menuPortalTarget}
                options={options}
                isMulti
                onMenuClose={onMenuClose}
                hideSelectedOptions={false}
                isSearchable={true}
                isOptionDisabled={(option) => option.isDisabled === true}
                value={selectedOptions}
                isClearable={isClearable}
                isDisabled={disabled}
                onChange={onChange}
                placeholder={placeholder}
                styles={{
                    placeholder: (provided) => ({
                        ...provided,
                        color: "#0000008a",
                    }),
                    container: (provided) => ({
                        ...provided,
                        width: width,
                    }),
                    control: (provided) => ({
                        ...provided,
                        width: "100%",
                        height: 32,
                        minHeight: 32,
                    }),
                    option: (provided) => ({
                        ...provided,
                        backgroundColor: "#fff",
                        color: "#000",
                        ":hover": {
                            backgroundColor: "#f5f5f5",
                        },
                    }),
                    indicatorsContainer: (styles) => ({
                        ...styles,
                        paddingTop: 1,
                        paddingBottom: 1,
                    }),
                    dropdownIndicator: (styles) => ({
                        ...styles,
                        paddingTop: 1,
                        paddingBottom: 1,
                    }),
                    clearIndicator: (styles) => ({
                        ...styles,
                        paddingTop: 1,
                        paddingBottom: 1,
                    }),

                    ...customStyles,
                }}
                closeMenuOnSelect={false}
                components={{
                    Option,
                    ValueContainer: CustomValueContainer,
                }}
            />
        </div>
    )
}

export default ReactMultiSelect

const CustomValueContainer = ({
    children,
    ...props
}: ValueContainerProps<ReactMultiSelectOptionT, true>) => {
    const { getValue } = props
    const selectedCount = getValue().length

    return (
        <components.ValueContainer {...props}>
            <span style={{ marginRight: "8px" }}>
                {selectedCount > 0 ? `${selectedCount} selected` : ""}
            </span>
            {React.Children.map(children, (child) =>
                child &&
                (child as React.ReactElement).type !== components.MultiValue
                    ? child
                    : null
            )}
        </components.ValueContainer>
    )
}

const Option = (props: OptionProps<ReactMultiSelectOptionT>) => {
    const { isSelected, data } = props
    const value = data.label

    const icon = isSelected ? faCheck : faSquare
    const color = isSelected ? "#0DA700" : "#1e88e5"

    return (
        <components.Option {...props}>
            <div
                style={{
                    display: "flex",
                }}
            >
                <FontAwesomeIcon
                    icon={icon}
                    style={{ marginRight: "8px", color: color }}
                />
                <span>{value}</span>
            </div>
        </components.Option>
    )
}
